/**
 * AdminUserCategories — manage the "I Am A" list (super-admin).
 *
 * Single-level managed list (mirrors AdminLocations, simpler). The values here
 * populate the "I Am A" select on the guest form and the user profile page.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Trash2, Save, X, Tags } from 'lucide-react';
import { TranslationFields } from '../../components/admin/TranslationFields';
import { saveTranslations, deleteTranslations, type TranslationValues } from '../../services/translationsApi';
import {
  AdminUserCategory,
  fetchAdminUserCategories,
  createUserCategory,
  updateUserCategory,
  deleteUserCategory,
} from '../../services/userCategoriesApi';

export const AdminUserCategories: React.FC = () => {
  const [items, setItems] = useState<AdminUserCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);

  const [editId, setEditId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  // Per-locale category names, reset each time a different row is opened.
  const [translations, setTranslations] = useState<TranslationValues>({});
  const [editOrder, setEditOrder] = useState<string>('');
  const [savingId, setSavingId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const flash = (msg: string) => { setNotice(msg); setTimeout(() => setNotice(null), 4000); };

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await fetchAdminUserCategories());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    setAdding(true);
    setError(null);
    try {
      await createUserCategory({ name });
      setNewName('');
      flash(`Added "${name}"`);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add');
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (c: AdminUserCategory) => {
    setEditId(c.id);
    setEditName(c.name);
    setTranslations({});
    setEditOrder(c.sortOrder === null ? '' : String(c.sortOrder));
  };

  const saveEdit = async (id: number) => {
    const name = editName.trim();
    if (!name) return;
    setSavingId(id);
    setError(null);
    try {
      await updateUserCategory(id, {
        name,
        sortOrder: editOrder.trim() === '' ? null : Number(editOrder),
      });
      // Same submit as the English, so a row can never be half-saved.
      await saveTranslations('user_categories', id, translations);
      setEditId(null);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSavingId(null);
    }
  };

  const toggleActive = async (c: AdminUserCategory) => {
    setSavingId(c.id);
    try {
      await updateUserCategory(c.id, { isActive: !c.isActive });
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update');
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (id: number) => {
    setSavingId(id);
    try {
      await deleteUserCategory(id);
      // Drop translations too: entity_id is an AUTO_INCREMENT value, so orphans
      // would eventually attach to an unrelated future category.
      await deleteTranslations('user_categories', id).catch(() => {});
      setConfirmDeleteId(null);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="bg-white rounded-3 shadow-sm p-4">
      <h2 className="fw-bold mb-1 d-flex align-items-center gap-2"><Tags size={20} /> User Categories</h2>
      <p className="text-muted small mb-4">
        The "I Am A" options shown on the estimate form and the user profile page.
        Order: a number pins it; blank sorts alphabetically. Inactive items are hidden from users.
      </p>

      {error && <div className="alert alert-warning py-2 small">{error}</div>}
      {notice && <div className="alert alert-success py-2 small">{notice}</div>}

      <div className="d-flex gap-2 mb-4" style={{ maxWidth: 480 }}>
        <input
          type="text"
          className="form-control"
          placeholder="New category name (e.g. Contractor)"
          value={newName}
          disabled={adding}
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
        />
        <button className="btn btn-primary d-inline-flex align-items-center gap-1" disabled={adding || !newName.trim()} onClick={handleAdd}>
          {adding ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Plus size={16} />} Add
        </button>
      </div>

      {loading ? (
        <div className="text-center py-4"><Loader2 size={22} className="text-muted" style={{ animation: 'spin 1s linear infinite' }} /></div>
      ) : (
        <div className="table-responsive">
          <table className="table align-middle">
            <thead>
              <tr className="small text-muted">
                <th>Name</th><th style={{ width: 90 }}>Order</th><th style={{ width: 90 }}>Active</th><th style={{ width: 160 }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map(c => {
                const editing = editId === c.id;
                const busy = savingId === c.id;
                return (
                  <tr key={c.id}>
                    <td>
                      {editing ? (
                        <input className="form-control form-control-sm" value={editName} onChange={e => setEditName(e.target.value)} />
                      ) : (
                        <><span className="fw-semibold">{c.name}</span> <code className="text-muted small ms-1">{c.slug}</code></>
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <input className="form-control form-control-sm" style={{ width: 70 }} value={editOrder} placeholder="—" onChange={e => setEditOrder(e.target.value)} />
                      ) : (c.sortOrder ?? '—')}
                    </td>
                    <td>
                      <div className="form-check form-switch m-0">
                        <input type="checkbox" role="switch" className="form-check-input" checked={c.isActive} disabled={busy} onChange={() => toggleActive(c)} />
                      </div>
                    </td>
                    <td className="text-end">
                      {editing ? (
                        <>
                          <button className="btn btn-sm btn-primary me-1" disabled={busy} onClick={() => saveEdit(c.id)}>
                            {busy ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={14} />}
                          </button>
                          <button className="btn btn-sm btn-outline-secondary" disabled={busy} onClick={() => setEditId(null)}><X size={14} /></button>
                        </>
                      ) : confirmDeleteId === c.id ? (
                        <>
                          <button className="btn btn-sm btn-danger me-1" disabled={busy} onClick={() => handleDelete(c.id)}>Delete</button>
                          <button className="btn btn-sm btn-outline-secondary" disabled={busy} onClick={() => setConfirmDeleteId(null)}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => startEdit(c)}>Edit</button>
                          <button className="btn btn-sm btn-outline-danger" onClick={() => setConfirmDeleteId(c.id)}><Trash2 size={14} /></button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              }).flatMap((row, i) => {
                const c = items[i];
                if (editId !== c.id) return [row];
                return [row, (
                  <tr key={`tr-${c.id}`}>
                    <td colSpan={4} className="pt-0">
                      <TranslationFields
                        entityType="user_categories"
                        entityId={c.id}
                        fields={[{ path: 'name', label: 'Category name', english: editName }]}
                        values={translations}
                        onChange={setTranslations}
                      />
                    </td>
                  </tr>
                )];
              })}
              {items.length === 0 && (
                <tr><td colSpan={4} className="text-muted small text-center py-3">No categories yet — add one above.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminUserCategories;
